import moment from 'moment'
import { flatten, groupBy, isNil, isObject, noop, uniqBy as uniq } from 'lodash-es'
import { createSelector, Selector } from '@reduxjs/toolkit'
import { filter, map, pipe, sortBy, uniqBy } from 'lodash/fp'
import { GlobalState } from 'store'
import { sha1 } from 'hash.js'
import {
  AuditDetail,
  Collateral,
  CollateralBasket,
  CollectionDetail,
  EntityType,
  IsBestQuote,
  Negotiation,
  NegotiationDetail,
  NegotiationDetailWithMeta,
  NegotiationEvent,
  NegotiationHistorySubscriptionIdentifier,
  NegotiationPackageDetail,
  NegotiationSide,
  NegotiationStatus,
  NegotiationSubscriptionIdentifier,
  NegotiationType,
  NegotiationUpdate,
  Permission,
  Product,
  ProductSector,
  RfqType,
  TradeConditionFlag,
  TradingEntity,
  TradingEntityWithAlias
} from 'api'
import { RepositoryEntry } from 'store/repository'
import { authorisedUserNameSelector, authorisedUserPermissionsSelector } from '../connectionSelectors'
import { currentOrganisationIdSelector, isOwnTradingEntityGetterSelector } from '../adminSelectors'
import { createEntityGetterSelector } from '../entitySelectors'
import { createUserByUsernameGetterSelector } from '../userSelectors'
import { createSelectedCollectionIdSelector } from '../selectionSelectors'
import { hideExpiredRejectedSelector, showOrgActivitySelector } from '../gridSelectors'
import { createIsInitiatedNegotiationCounterpartyViewEnabled, userPreferenceOtwDurationSelector } from '../userPreferenceSelectors'
import {
  createTradingEntityWithAliasGetterSelector,
  hasUsSponsoredRelationshipSelector
} from '../tradingEntitySelectors/tradingEntitySelectors'
import {
  aggregateAllOrNothing,
  getCurrentWindowId,
  MULTIPLE_VALUES,
  notNilFilter,
  ParametricSelector,
  createDeepEqualSelector
} from 'utils'
import {
  applyUpdatesForWindow,
  createSponsorFromCounterparty,
  getMetaValueForWindow,
  newCollectionDetail,
  sortRfqEndTimeDescAndUploadRowIndexAsc,
  toCollectionDetails,
  TradingEntityWithAliasHelper,
  UnassignedCollectionId,
  UnassignedCollectionName
} from 'model'
import { GroupRowData } from 'view/components/datagrid'
import { LiveGridColumnId, NegotiationFieldNames } from 'view/routes/requests/LiveNegotiationsGrid/LiveGridColumnNames'
import { CollectionColumnId } from 'view/routes/ColumnNames'
import { GridId } from 'store/grid'
import {
  canAccept,
  existsMultiEntityCollection,
  getNegotiatedValue,
  isExcludedFromBestPriceCalculations,
  itemNegotiationTypes,
  summaryNegotiationTypes
} from 'view/routes/requests/shared/utils'
import { NegotiationFilter, NegotiationSelectionOptions } from './negotiationSelectorTypes'
import { NEGOTIATIONS_PATH } from 'view/components/datagrid/renderers/negotiationDataUtil'
import { createNegotiationHistoryDetailsSelector } from '../auditSelectors'
import { Dictionary, first, keyBy } from 'lodash'
import { isNotNil } from 'utils/predicate'
import { negotiationRepositoryEntryGetterSelector } from './negotiationRepositoryEntrySelectors'
import { DEFAULT_INITIATED_SUMMARY_MAP_KEY } from './constants'
import { bestPriceByNegotiationIdGetterSelector, isBestPriceEligibleNegotiation } from 'selectors/bestPriceSelectors'

const createInitiatedNegotiationsStatusSelector = (forceHideExpiredRejected?: boolean) =>
  createSelector(hideExpiredRejectedSelector, hideExpiredRejected => {
    const baseInitiatedNegotiationsStatuses = [
      NegotiationStatus.ITEMS_IN_DIFFERENT_STATES,
      NegotiationStatus.REQUESTED_WITHOUT_PRICE,
      NegotiationStatus.REQUESTED_WITH_PRICE,
      NegotiationStatus.QUOTED,
      NegotiationStatus.QUOTED_SUBJECT,
      NegotiationStatus.WITHDRAWN
    ]

    return hideExpiredRejected || forceHideExpiredRejected
      ? baseInitiatedNegotiationsStatuses
      : [
          ...baseInitiatedNegotiationsStatuses,
          NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_REJECTED_EXPIRED,
          NegotiationStatus.EXPIRED,
          NegotiationStatus.REJECTED
        ]
  })

export const incomingNegotiationsStatusSelector = createSelector(noop, () => [
  NegotiationStatus.ITEMS_IN_DIFFERENT_STATES,
  NegotiationStatus.REQUESTED_WITHOUT_PRICE,
  NegotiationStatus.REQUESTED_WITH_PRICE,
  NegotiationStatus.QUOTED,
  NegotiationStatus.QUOTED_SUBJECT,
  NegotiationStatus.WITHDRAWN
])

const topLevelNegotiationTypes = [
  NegotiationType.INDIVIDUAL,
  NegotiationType.CONTINGENT_SUMMARY,
  NegotiationType.ONE_CANCELS_OTHERS_SUMMARY,
  NegotiationType.SMART_GROUPING_SUMMARY
]

/* General Selectors */

const negotiationFilteredRepositoryEntryGetterSelector = createSelector(
  negotiationRepositoryEntryGetterSelector,
  isOwnTradingEntityGetterSelector,
  authorisedUserPermissionsSelector,
  (getNegotiationRepositoryEntries, getIsOwnTradingEntity, userPermissions) => (negotiationFilter: NegotiationFilter) => {
    const allInitiatorEntries = getNegotiationRepositoryEntries(NegotiationSide.INITIATOR)
    const allResponderEntries = getNegotiationRepositoryEntries(NegotiationSide.RESPONDER)

    // create set of the keys of all qualifying initiator negotiations
    const initiatorKeys =
      negotiationFilter === NegotiationFilter.InitiatorOnly || negotiationFilter === NegotiationFilter.All
        ? new Set(Object.keys(allInitiatorEntries).filter(key => getIsOwnTradingEntity(allInitiatorEntries[key].lastUpdate.entity.fundId)))
        : new Set<string>()

    // create set of the keys of all qualifying responder negotiations
    const responderKeys =
      negotiationFilter === NegotiationFilter.ResponderOnly || negotiationFilter === NegotiationFilter.All
        ? new Set(
            Object.keys(allResponderEntries).filter(key => {
              const counterpartyValue = getNegotiatedValue(allResponderEntries[key].lastUpdate.entity.counterpartyIdNegotiableField)
              return counterpartyValue ? getIsOwnTradingEntity(counterpartyValue) : false
            })
          )
        : new Set<string>()

    // remove possible duplicate keys, favouring either initiator or responder side
    // if the user can see initiated negotiations we favour that side
    if (userPermissions.includes(Permission.INITIATED_RFQS_VIEW) && responderKeys.size > 0) {
      initiatorKeys.forEach(key => {
        if (responderKeys.has(key)) {
          responderKeys.delete(key)
        }
      })
    } else if (initiatorKeys.size > 0) {
      responderKeys.forEach(key => {
        if (initiatorKeys.has(key)) {
          initiatorKeys.delete(key)
        }
      })
    }

    const initiatorEntries = Array.from(initiatorKeys).map(key => allInitiatorEntries[key])
    const responderEntries = Array.from(responderKeys).map(key => allResponderEntries[key])

    return [...initiatorEntries, ...responderEntries]
  }
)

export function createNegotiationEntrySelector(
  options: NegotiationSelectionOptions
): Selector<GlobalState, RepositoryEntry<NegotiationUpdate>[]> {
  const { negotiationFilter, negotiationTypes, hideNegotiationSentToStaging, subscriptionIdentifier } = options
  const statusSelector = options.statusSelector || createSelector<any, NegotiationStatus[] | null>(noop, () => null)
  const collectionIdSelector = options.collectionIdSelector || createSelector<any, string | null>(noop, () => null)

  return createSelector(
    [negotiationFilteredRepositoryEntryGetterSelector, statusSelector, collectionIdSelector],
    (getNegotiationFilteredRepositoryEntries, statuses, collectionIdOrUnassigned) => {
      const negotiationRepositoryEntries = getNegotiationFilteredRepositoryEntries(negotiationFilter).filter(entry => {
        const negotiation = entry.lastUpdate.entity
        const ownOrder = entry.lastUpdate.negotiationSide === NegotiationSide.INITIATOR

        if (hideNegotiationSentToStaging && negotiation.sentBackToStaging) {
          return false
        }

        if (negotiationTypes && negotiationTypes.length > 0 && !negotiationTypes.includes(negotiation.negotiationType)) {
          return false
        }

        if (collectionIdOrUnassigned) {
          const collectionId = collectionIdOrUnassigned === UnassignedCollectionId ? null : collectionIdOrUnassigned

          if (
            (ownOrder && negotiation.containerCollectionId !== collectionIdOrUnassigned && negotiation.collectionId !== collectionId) ||
            (!ownOrder && negotiation.collectionId !== collectionId)
          ) {
            return false
          }
        }

        if (statuses && !statuses.includes(negotiation.status)) {
          return false
        }

        if (
          (negotiation.status === NegotiationStatus.EXPIRED ||
            negotiation.status === NegotiationStatus.REJECTED ||
            negotiation.status === NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_REJECTED_EXPIRED) &&
          !negotiation.canSendBackToStaging
        ) {
          return false
        }

        if (subscriptionIdentifier && !entry.lastUpdate.subscriptionIdentifiers.includes(subscriptionIdentifier)) {
          return false
        }

        return true
      })

      return sortRfqEndTimeDescAndUploadRowIndexAsc(negotiationRepositoryEntries)
    }
  )
}

export function createOrgActivityFilteredNegotiationEntrySelector(
  options: NegotiationSelectionOptions
): Selector<GlobalState, RepositoryEntry<NegotiationUpdate>[]> {
  return createSelector(
    createNegotiationEntrySelector(options),
    authorisedUserNameSelector,
    showOrgActivitySelector,
    (negotiationEntries, username, showOrgActivity) => {
      return negotiationEntries.filter(entry => {
        if (showOrgActivity) {
          return true
        }

        const negotiation = entry.lastUpdate.entity
        return negotiation.createdBy === username
      })
    }
  )
}

export const createNegotiationResponderTradingEntityWithAliasGetterSelector: Selector<
  GlobalState,
  (responderId: string, negotiation: Negotiation) => TradingEntityWithAlias | null
> = createSelector(
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  getTradingEntity => (responderId: string, negotiation: Negotiation) => {
    const tradingEntity = getTradingEntity(responderId)
    const alias = negotiation.counterpartyAlias

    return tradingEntity ? { ...tradingEntity, alias: alias } : null
  }
)

export function createNegotiationDetailsSelector(
  negotiationEntriesSelector: Selector<GlobalState, RepositoryEntry<NegotiationUpdate>[]>
): Selector<GlobalState, NegotiationDetail[]> {
  return createSelector(
    negotiationEntriesSelector,
    authorisedUserNameSelector,
    userPreferenceOtwDurationSelector,
    createEntityGetterSelector(EntityType.COLLECTION),
    createEntityGetterSelector(EntityType.COLLATERAL),
    createEntityGetterSelector(EntityType.COLLATERAL_BASKET),
    createEntityGetterSelector(EntityType.CLEARING_HOUSE),
    createEntityGetterSelector(EntityType.TRADING_ENTITY),
    createNegotiationResponderTradingEntityWithAliasGetterSelector,
    createTradingEntityWithAliasGetterSelector(),
    createEntityGetterSelector(EntityType.ORGANISATION),
    createEntityGetterSelector(EntityType.PRODUCT),
    createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
    createEntityGetterSelector(EntityType.REFERENCE_RATE),
    createEntityGetterSelector(EntityType.SPONSOR),
    createEntityGetterSelector(EntityType.USER),
    createEntityGetterSelector(EntityType.NOTES),
    createUserByUsernameGetterSelector,
    hasUsSponsoredRelationshipSelector,
    (
      negotiationEntries,
      authorisedUserName,
      otwDurationMinsDefault,
      getCollection,
      getCollateral,
      getCollateralBasket,
      getClearingHouse,
      getTradingEntity,
      getNegotiationTradingEntityWithAlias,
      getTradingEntityWithAlias,
      getOrganisation,
      getProduct,
      getProductSector,
      getReferenceRate,
      getSponsor,
      getUser,
      getNotes,
      getUserByUsername,
      hasUsSponsoredRelationship
    ) => {
      return negotiationEntries.map(negotiationEntry => {
        const negotiation: Negotiation = applyUpdatesForWindow<NegotiationUpdate>(negotiationEntry, getCurrentWindowId())
        const ownOrder = negotiationEntry.lastUpdate.negotiationSide === NegotiationSide.INITIATOR
        const collection = negotiation.collectionId ? getCollection(negotiation.collectionId) : null
        const containerCollection = negotiation.containerCollectionId ? getCollection(negotiation.containerCollectionId) : null

        const collateral = negotiation.collateralId ? getCollateral(negotiation.collateralId) : null
        const collateralBasket = negotiation.basketAllocation ? getCollateralBasket(negotiation.basketAllocation.basketId) : null
        const productSector = negotiation.productSectorId ? getProductSector(negotiation.productSectorId) : null
        const product = negotiation.productId ? getProduct(negotiation.productId) : null

        const counterpartyValue = getNegotiatedValue<string>(negotiation.counterpartyIdNegotiableField)

        const counterparty = counterpartyValue
          ? ownOrder
            ? getNegotiationTradingEntityWithAlias(counterpartyValue, negotiation)
            : getTradingEntity(counterpartyValue)
          : null

        const counterpartyFirm = counterparty ? getOrganisation(counterparty.organisationId) : null

        const orderFund = negotiation.fundId
          ? ownOrder
            ? getTradingEntity(negotiation.fundId)
            : counterpartyValue
            ? getTradingEntityWithAlias(counterpartyValue, negotiation.fundId)
            : null
          : null

        const orderFundFirm = orderFund ? getOrganisation(orderFund.organisationId) : null

        const clearingHouse = negotiation.clearingHouseId ? getClearingHouse(negotiation.clearingHouseId) : null
        const sponsor = negotiation.sponsorId
          ? negotiation.isSponsorCounterparty
            ? createSponsorFromCounterparty(counterparty)
            : getSponsor(negotiation.sponsorId)
          : null
        const counterpartySponsorId = getNegotiatedValue(negotiation.counterpartySponsorId)
        const counterpartySponsor = counterpartySponsorId ? getSponsor(counterpartySponsorId) : null

        const referenceRate = negotiation.referenceRate ? getReferenceRate(negotiation.referenceRate) : null
        const floatCrystallisationDays = negotiation.floatCrystallisationDays
        const userWithLock = negotiation.userIdWithLock ? getUserByUsername(negotiation.userIdWithLock) : null
        const axeTradeCondition = {
          isAxed: !!getNegotiatedValue(negotiation.isAxed),
          tradeCondition: getNegotiatedValue(negotiation.tradeConditionFlag) || TradeConditionFlag.NONE
        }
        const otwDurationMeta = getMetaValueForWindow<number>(negotiationEntry, NegotiationFieldNames.OTW_DURATION, getCurrentWindowId())

        const otwDuration =
          otwDurationMeta === undefined
            ? getNegotiatedValue(negotiation.rfqType) === RfqType.INDICATIVE
              ? null
              : otwDurationMinsDefault
            : otwDurationMeta
        const notes = negotiation.notesId ? getNotes(negotiation.notesId) : null

        const validationIssues = negotiationEntry.lastUpdate.validationIssues
        const availableEvents = getAvailableEvents(negotiationEntry.lastUpdate, authorisedUserName)

        const initiatorExecutionDecisionMaker = negotiation.initiatorExecutionDecisionMaker
          ? getUser(getNegotiatedValue(negotiation.initiatorExecutionDecisionMaker)!)
          : null
        const initiatorInvestmentDecisionMaker = negotiation.initiatorInvestmentDecisionMaker
          ? getUser(getNegotiatedValue(negotiation.initiatorInvestmentDecisionMaker)!)
          : null
        const responderExecutionDecisionMaker = negotiation.responderExecutionDecisionMaker
          ? getUser(getNegotiatedValue(negotiation.responderExecutionDecisionMaker)!)
          : null
        const responderInvestmentDecisionMaker = negotiation.responderInvestmentDecisionMaker
          ? getUser(getNegotiatedValue(negotiation.responderInvestmentDecisionMaker)!)
          : null
        const fundPrimarySalesCoverUser = negotiation.fundPrimaryCoverUserId ? getUser(negotiation.fundPrimaryCoverUserId) : null
        const fundSecondarySalesCoverUser = negotiation.fundSecondaryCoverUserId ? getUser(negotiation.fundSecondaryCoverUserId) : null
        const counterpartyPrimarySalesCoverUser = negotiation.counterpartyPrimaryCoverUserId
          ? getUser(negotiation.counterpartyPrimaryCoverUserId)
          : null
        const counterpartySecondarySalesCoverUser = negotiation.counterpartySecondaryCoverUserId
          ? getUser(negotiation.counterpartySecondaryCoverUserId)
          : null

        const excludeFromBestPriceCalculations = isExcludedFromBestPriceCalculations(negotiation)

        const { childFundIds, childCollateralIds, childBasketIds, childSecondaryBasketIds = [] } = negotiation.negotiationMeta
        const multipleFundIds = childFundIds.length > 1
        const multipleIsins = childCollateralIds.length > 1
        const multipleBasketIds = childBasketIds.length > 1
        const childFunds = multipleFundIds
          ? childFundIds
              .map(childFundId =>
                ownOrder
                  ? getTradingEntity(childFundId)
                  : counterpartyValue
                  ? getTradingEntityWithAlias(counterpartyValue, childFundId)
                  : null
              )
              .filter(notNilFilter)
          : []
        const childCollaterals = multipleIsins ? childCollateralIds.map(childIsin => getCollateral(childIsin)).filter(notNilFilter) : []
        const childBaskets = multipleBasketIds
          ? childBasketIds.map(childBasketId => getCollateralBasket(childBasketId)).filter(notNilFilter)
          : []
        const childProductSectors = childCollaterals
          ? childCollaterals.map(childCollateral => getProductSector(childCollateral.productSectorId)).filter(notNilFilter)
          : childBaskets
          ? childBaskets.map(childBasket => getProductSector(childBasket.productSectorId)).filter(notNilFilter)
          : []
        const childProducts = childProductSectors
          ? childProductSectors.map(childProductSector => getProduct(childProductSector.productId)).filter(notNilFilter)
          : []
        const orderFundAgg = aggregateOrderFund(childFunds)
        const collateralAgg = aggregateCollateral(childCollaterals)
        const collateralBasketAgg = aggregateCollateralBasket(childBaskets)
        const productSectorAgg = aggregateProductSector(childProductSectors)
        const productAgg = aggregateProduct(childProducts)

        const secondaryInstrumentName = negotiation.secondaryBasketId
          ? getCollateralBasket(getNegotiatedValue(negotiation.secondaryBasketId)!)?.name ?? null
          : negotiation.secondaryCollateralId
          ? getCollateral(getNegotiatedValue(negotiation.secondaryCollateralId)!)?.name ?? null
          : null

        const multipleSecondaryBaskets = childSecondaryBasketIds.length > 0
        const secondaryBasketAgg = aggregateSecondaryInstrument(
          childSecondaryBasketIds.map(basketId => getCollateralBasket(basketId)).filter(isNotNil)
        )

        const usSponsoredRelationship = orderFund && counterparty ? hasUsSponsoredRelationship(orderFund.id, counterparty.id) : false

        const isLinked = negotiation.servicingTrigger && getNegotiatedValue(negotiation.servicingTrigger.linked)
        const linkedTradeId = isLinked ? negotiation.servicingTrigger?.previousDealId ?? null : null

        return {
          negotiation,
          direction: negotiation.direction,
          containerCollection,
          collection,
          collateral: multipleIsins ? collateralAgg : collateral,
          collateralBasket: multipleBasketIds ? collateralBasketAgg : collateralBasket,
          clearingHouse,
          sponsor,
          counterpartySponsor,
          productSector: multipleIsins || multipleBasketIds ? productSectorAgg : productSector,
          product: multipleIsins || multipleBasketIds ? productAgg : product,
          counterparty,
          counterpartyFirm,
          orderFund: multipleFundIds ? orderFundAgg : orderFund,
          orderFundFirm,
          referenceRate,
          floatCrystallisationDays,
          userWithLock,
          axeTradeCondition,
          ownOrder,
          excludeFromBestPriceCalculations,
          otwDuration,
          notes,
          initiatorExecutionDecisionMaker,
          initiatorInvestmentDecisionMaker,
          responderExecutionDecisionMaker,
          responderInvestmentDecisionMaker,
          fundPrimarySalesCoverUser,
          fundSecondarySalesCoverUser,
          counterpartyPrimarySalesCoverUser,
          counterpartySecondarySalesCoverUser,
          validationIssues,
          availableEvents,
          secondaryInstrumentName: multipleSecondaryBaskets ? secondaryBasketAgg?.name ?? null : secondaryInstrumentName,
          usSponsoredRelationship,
          linkedTradeId
        }
      })
    }
  )
}

function aggregateOrderFund(funds: (TradingEntity | TradingEntityWithAlias)[]): TradingEntity | TradingEntityWithAlias | null {
  return funds.length > 0
    ? {
        ...funds[0],
        bic: MULTIPLE_VALUES,
        lei: MULTIPLE_VALUES,
        publishedName: MULTIPLE_VALUES,
        shortName: MULTIPLE_VALUES,
        alias: funds.some(fund => isTradingEntityWithAlias(fund)) ? MULTIPLE_VALUES : undefined
      }
    : null
}

function aggregateCollateral(collaterals: Collateral[]): Collateral | null {
  return collaterals.length > 0
    ? {
        ...collaterals[0],
        id: MULTIPLE_VALUES,
        name: MULTIPLE_VALUES,
        cusip: MULTIPLE_VALUES,
        isin: MULTIPLE_VALUES,
        currency: aggregateAllOrNothing(collaterals, collateral => collateral.currency, null!),
        issuer: aggregateAllOrNothing(collaterals, collateral => collateral.issuer, MULTIPLE_VALUES),
        coupon: aggregateAllOrNothing(collaterals, collateral => collateral.coupon, null!)
      }
    : null
}

function aggregateCollateralBasket(collateralBaskets: CollateralBasket[]): CollateralBasket | null {
  return collateralBaskets.length > 0
    ? {
        ...collateralBaskets[0],
        collateralBasketIsin: MULTIPLE_VALUES,
        dbvClass: aggregateAllOrNothing(collateralBaskets, collateralBasket => collateralBasket.dbvClass, MULTIPLE_VALUES),
        id: MULTIPLE_VALUES,
        name: MULTIPLE_VALUES,
        description: aggregateAllOrNothing(collateralBaskets, collateralBasket => collateralBasket.description, MULTIPLE_VALUES)
      }
    : null
}

function aggregateProduct(products: Product[]): Product | null {
  return products.length > 0
    ? {
        ...products[0],
        name: aggregateAllOrNothing(products, product => product.name, MULTIPLE_VALUES)
      }
    : null
}

function aggregateProductSector(productSectors: ProductSector[]): ProductSector | null {
  return productSectors.length > 0
    ? {
        ...productSectors[0],
        name: aggregateAllOrNothing(productSectors, productSector => productSector.name, MULTIPLE_VALUES)
      }
    : null
}

function aggregateSecondaryInstrument<T extends Collateral | CollateralBasket>(instruments: T[]): T | null {
  return instruments.length > 0
    ? {
        ...instruments[0],
        name: aggregateAllOrNothing(instruments, i => i.name, null)
      }
    : null
}

export function createNegotiationDetailSelector<TProps>(
  negotiationIdSelector: ParametricSelector<{}, TProps, string>,
  ownOrderSelector: ParametricSelector<{}, TProps, boolean>,
  historySelector: ParametricSelector<{}, TProps, boolean>
): ParametricSelector<GlobalState, TProps, NegotiationDetail | AuditDetail | undefined> {
  const negotiationInitiatorEntrySelector = createNegotiationEntrySelector({ negotiationFilter: NegotiationFilter.InitiatorOnly })
  const negotiationResponderEntrySelector = createNegotiationEntrySelector({ negotiationFilter: NegotiationFilter.ResponderOnly })
  return createSelector(
    createNegotiationDetailsSelector(negotiationInitiatorEntrySelector),
    createNegotiationDetailsSelector(negotiationResponderEntrySelector),
    createNegotiationHistoryDetailsSelector([
      NegotiationHistorySubscriptionIdentifier.LATEST,
      NegotiationHistorySubscriptionIdentifier.HISTORICAL
    ]),
    negotiationIdSelector,
    ownOrderSelector,
    historySelector,
    (negotiationInitiatorDetails, negotiationResponderDetails, negotiationHistoryDetails, negotiationId, ownOrder, history) => {
      if (history) {
        return negotiationHistoryDetails.find(
          negotiationHistoryDetail => negotiationId === negotiationHistoryDetail.negotiation.negotiationId
        )
      }

      const negotiationDetails = ownOrder ? negotiationInitiatorDetails : negotiationResponderDetails
      return negotiationDetails.find(negotiationDetail => negotiationId === negotiationDetail.negotiation.negotiationId)
    }
  )
}

export function createFilteredNegotiationDetailsSelector<TProps>(
  negotiationIdsSelector: ParametricSelector<{}, TProps, string[]>,
  ownOrderSelector: ParametricSelector<{}, TProps, boolean>
): ParametricSelector<GlobalState, TProps, NegotiationDetail[]> {
  const negotiationInitiatorEntrySelector = createNegotiationEntrySelector({ negotiationFilter: NegotiationFilter.InitiatorOnly })
  const negotiationResponderEntrySelector = createNegotiationEntrySelector({ negotiationFilter: NegotiationFilter.ResponderOnly })
  return createSelector(
    createNegotiationDetailsSelector(negotiationInitiatorEntrySelector),
    createNegotiationDetailsSelector(negotiationResponderEntrySelector),
    negotiationIdsSelector,
    ownOrderSelector,
    (negotiationInitiatorDetails, negotiationResponderDetails, negotiationIds, ownOrder) => {
      const negotiationDetails = ownOrder ? negotiationInitiatorDetails : negotiationResponderDetails
      return negotiationDetails.filter(negotiationDetail => negotiationIds.includes(negotiationDetail.negotiation.negotiationId))
    }
  )
}

export function getAvailableEvents(negotiationUpdate: NegotiationUpdate, authorisedUserName: string): NegotiationEvent[] {
  if (negotiationUpdate.negotiationSide === NegotiationSide.INITIATOR) {
    return negotiationUpdate.initiatorAvailableEvents
  }

  if (authorisedUserName === negotiationUpdate.entity.userIdWithLock) {
    return negotiationUpdate.responderWithLockAvailableEvents
  }

  return negotiationUpdate.responderWithoutLockAvailableEvents
}

/* Responder-side Selectors */

export const isNegotiationCurve = (n: Negotiation) => n.isCurve === true

export function createIncomingNegotiationsSelector(negotiationTypes?: NegotiationType[]): Selector<GlobalState, NegotiationDetail[]> {
  const negotiationEntrySelector = createNegotiationEntrySelector({
    negotiationFilter: NegotiationFilter.ResponderOnly,
    statusSelector: incomingNegotiationsStatusSelector,
    collectionIdSelector: createSelectedCollectionIdSelector(GridId.IncomingGrid),
    negotiationTypes,
    subscriptionIdentifier: NegotiationSubscriptionIdentifier.INCOMING
  })
  return createNegotiationDetailsSelector(negotiationEntrySelector)
}

export function createIncomingItemNegotiationsSelector(): Selector<GlobalState, NegotiationDetail[]> {
  return createIncomingNegotiationsSelector(itemNegotiationTypes)
}

function createIncomingSummaryNegotiationsSelector(): Selector<GlobalState, NegotiationDetail[]> {
  return createIncomingNegotiationsSelector(summaryNegotiationTypes)
}

export function createIncomingSummaryNegotiationsMapSelector(): Selector<GlobalState, GroupRowData<NegotiationDetail> | undefined> {
  return createSelector(
    createIncomingSummaryNegotiationsSelector(),
    createIncomingItemNegotiationsSelector(),
    (incomingSummaryNegotiationDetails, incomingItemNegotiationDetails) => {
      const mapKey = existsMultiEntityCollection(incomingItemNegotiationDetails)
        ? `${CollectionColumnId.COLLECTION_NAME}-${LiveGridColumnId.FUND_NAME}`
        : CollectionColumnId.COLLECTION_NAME

      return incomingSummaryNegotiationDetails.length > 0
        ? {
            [mapKey]: incomingSummaryNegotiationDetails.reduce((acc, negotiationDetail) => {
              if (!negotiationDetail.ownOrder && negotiationDetail.negotiation.negotiationType !== NegotiationType.SMART_GROUPING_SUMMARY) {
                acc[negotiationDetail.negotiation.negotiationId] = negotiationDetail
              }
              return acc
            }, {} as Record<string, NegotiationDetail>)
          }
        : undefined
    }
  )
}

export const createChildOrParentIncomingNegotiationClearingHouseIdSelector = <TProps>(
  negotiationDetailSelector: ParametricSelector<GlobalState, TProps, NegotiationDetail>
) =>
  createSelector([negotiationDetailSelector, createIncomingItemNegotiationsSelector()], (negotiationDetail, incomingNegotiationItems) => {
    const childNegotiationIds = negotiationDetail.negotiation.negotiationMeta.childNegotiationIds

    const firstNonNilChildClearingHouseId = first(
      incomingNegotiationItems
        .filter(n => childNegotiationIds.includes(n.negotiation.negotiationId))
        .map(n => n.clearingHouse?.id)
        .filter(isNotNil)
    )

    return firstNonNilChildClearingHouseId ?? negotiationDetail.clearingHouse?.id ?? null
  })

/* Initiator-side Selectors */

export function createInitiatedNegotiationsSelector(
  negotiationTypes?: NegotiationType[],
  forceHideExpiredRejected?: boolean
): Selector<GlobalState, NegotiationDetail[]> {
  const negotiationEntrySelector = createOrgActivityFilteredNegotiationEntrySelector({
    negotiationFilter: NegotiationFilter.InitiatorOnly,
    statusSelector: createInitiatedNegotiationsStatusSelector(forceHideExpiredRejected),
    collectionIdSelector: createSelectedCollectionIdSelector(GridId.InitiatedGrid),
    negotiationTypes,
    hideNegotiationSentToStaging: true,
    subscriptionIdentifier: NegotiationSubscriptionIdentifier.INITIATED
  })
  return createNegotiationDetailsSelector(negotiationEntrySelector)
}

export const createInitiatedTopLevelNegotiationsSelector = (forceHideExpiredRejected?: boolean) =>
  createInitiatedNegotiationsSelector(topLevelNegotiationTypes, forceHideExpiredRejected)

export const initiatedItemNegotiationsSelector = createInitiatedNegotiationsSelector(itemNegotiationTypes)

export const initiatedSummaryNegotiationsSelector = createInitiatedNegotiationsSelector(summaryNegotiationTypes)

const createInitiatedSummaryNegotiationsMapSelector = (
  mapKey = DEFAULT_INITIATED_SUMMARY_MAP_KEY
): Selector<GlobalState, GroupRowData<NegotiationDetailWithMeta> | undefined> =>
  createSelector(
    initiatedSummaryNegotiationsSelector,
    bestPriceByNegotiationIdGetterSelector,
    (initiatedSummaryNegotiationDetails, getBestPriceByNegotiationId) =>
      initiatedSummaryNegotiationDetails.length > 0
        ? {
            [mapKey]: initiatedSummaryNegotiationDetails.reduce((acc, negotiationDetail) => {
              acc[negotiationDetail.negotiation.negotiationId] = {
                ...negotiationDetail,
                meta: {
                  isBestQuote: getBestPriceByNegotiationId(negotiationDetail.negotiation.negotiationId) ? IsBestQuote.YES : IsBestQuote.NO
                }
              }
              return acc
            }, {} as Record<string, NegotiationDetailWithMeta>)
          }
        : undefined
  )

const isInitiatedNegotiationCounterpartyViewEnabled = createIsInitiatedNegotiationCounterpartyViewEnabled()

export const userInitiatedSummaryNegotiationsMapSelector: Selector<
  GlobalState,
  GroupRowData<NegotiationDetailWithMeta> | undefined
> = createSelector(
  createInitiatedSummaryNegotiationsMapSelector(),
  createInitiatedSummaryNegotiationsMapSelector(CollectionColumnId.COLLECTION_NAME),
  isInitiatedNegotiationCounterpartyViewEnabled,
  (defaultSummaryNegotiationsMap, counterpartySummaryNegotiationsMap, isCounterpartyViewEnabled) =>
    isCounterpartyViewEnabled ? counterpartySummaryNegotiationsMap : defaultSummaryNegotiationsMap
)

export const createGroupedNegotiationsSelector = (
  negotiationsSelector: Selector<GlobalState, NegotiationDetailWithMeta[]>,
  groupByField: keyof Negotiation
) => createSelector(negotiationsSelector, negotiations => groupBy(negotiations, ({ negotiation }) => negotiation[groupByField]))

export const groupedByCounterpartyIdInitiatedNegotiationsSelector = createSelector(initiatedItemNegotiationsSelector, negotiations =>
  groupBy(negotiations, ({ negotiation }) => getNegotiatedValue(negotiation.counterpartyIdNegotiableField))
)

const createGroupedInitiatedNegotiationsSummarySelector = (groupByField: keyof Negotiation) =>
  createSelector(initiatedSummaryNegotiationsSelector, negotiations =>
    groupBy(negotiations, ({ negotiation }) => negotiation[groupByField])
  )

const initiatedItemNegotiationsSelectorWithMetaSelector: Selector<GlobalState, NegotiationDetailWithMeta[]> = createSelector(
  initiatedItemNegotiationsSelector,
  bestPriceByNegotiationIdGetterSelector,
  isBestPriceEligibleNegotiation,
  (negotiations, getBestPriceByNegotiationId, isBestPriceEligibleNegotiation) => {
    return negotiations.map(negotiation => ({
      ...negotiation,
      meta: {
        isBestQuote: getBestPriceByNegotiationId(negotiation.negotiation.negotiationId) ? IsBestQuote.YES : IsBestQuote.NO,
        isBestQuoteEligible: isBestPriceEligibleNegotiation(negotiation.negotiation.parentOrderId)
      }
    }))
  }
)

export const groupedByParentOrderIdInitiatedNegotiationsSelector = createGroupedNegotiationsSelector(
  createSelector(initiatedItemNegotiationsSelectorWithMetaSelector, itemNegotiations =>
    itemNegotiations.filter(n => n.counterparty?.id !== undefined)
  ),
  'parentOrderId'
)

export const groupedByCollectionIdInitiatedNegotiationsSummarySelector = createGroupedInitiatedNegotiationsSummarySelector('collectionId')

export const createNegotiationPackagesSelector = (
  groupedNegotiationsSelector: Selector<GlobalState, Dictionary<NegotiationDetail[]>>,
  packageKeyValueGetter: (n: NegotiationDetail) => string | undefined
) =>
  createSelector([groupedNegotiationsSelector], groupedNegotiations => {
    const mappedData = Object.values(groupedNegotiations).map(negotiationDetails => {
      const [firstNegotiation] = negotiationDetails
      const keyedNegotiationDetails = keyBy(negotiationDetails, packageKeyValueGetter)
      const uuid = sha1()
        .update(negotiationDetails.map(n => n.negotiation.negotiationId).join(''))
        .digest('hex')
      return {
        ...firstNegotiation,
        [NEGOTIATIONS_PATH]: keyedNegotiationDetails,
        uuid
      }
    })

    return mappedData
  })

export const initiatedNegotiationPackagesSelector = createNegotiationPackagesSelector(
  groupedByParentOrderIdInitiatedNegotiationsSelector,
  n => n.counterparty?.id
)

export const createInitiatedPackageNegotiationsSummarySelector = (collectionId?: string) =>
  createSelector(groupedByCollectionIdInitiatedNegotiationsSummarySelector, groupedNegotiationSummaries => {
    if (isNil(collectionId)) {
      return undefined
    }

    return groupedNegotiationSummaries[collectionId]
  })

export const hasInitiatedNegotiationCollectionAnyBestPriceSelector: Selector<
  GlobalState,
  (collectionId: string) => boolean
> = createSelector(initiatedItemNegotiationsSelectorWithMetaSelector, initiatedItems => {
  return (collectionId: string) => {
    const negotiationsWithinCollection = initiatedItems.filter(n => n.negotiation.collectionId === collectionId)

    return negotiationsWithinCollection.some(n => n.meta?.isBestQuote === IsBestQuote.YES)
  }
})

export const createUserInitiatedNegotiationsSelector: Selector<
  GlobalState,
  NegotiationDetailWithMeta[] | NegotiationPackageDetail[]
> = createSelector(
  initiatedItemNegotiationsSelectorWithMetaSelector,
  initiatedNegotiationPackagesSelector,
  isInitiatedNegotiationCounterpartyViewEnabled,
  (negotiations, groupedNegotiations, isCounterpartyViewEnabled) => (isCounterpartyViewEnabled ? groupedNegotiations : negotiations)
)

/* Counterparty View Selectors */

type CounterpartyColumnDetail = {
  id: string
  headerName: string
}

const mapCounterpartyToColumn = (entity: TradingEntityWithAlias): CounterpartyColumnDetail => ({
  id: TradingEntityWithAliasHelper.getId(entity),
  headerName: TradingEntityWithAliasHelper.getName(entity)
})

const isTradingEntityWithAlias = (counterparty: NegotiationDetail['counterparty']): counterparty is TradingEntityWithAlias =>
  isObject(counterparty)

const orderedNegotiationCounterPartiesSelector: Selector<GlobalState, CounterpartyColumnDetail[]> = createSelector(
  [initiatedItemNegotiationsSelector],
  pipe(
    map(negotiationDetail => ({ ...negotiationDetail.counterparty, alias: negotiationDetail.negotiation.counterpartyAlias })),
    filter(isTradingEntityWithAlias),
    map(mapCounterpartyToColumn),
    sortBy(({ headerName }) => headerName),
    uniqBy(({ id }) => id)
  )
)

export const createInitiatedCounterpartyColumnDetailSelector = (): Selector<GlobalState, CounterpartyColumnDetail[]> =>
  createDeepEqualSelector(orderedNegotiationCounterPartiesSelector, counterparties => {
    return counterparties
  })

/* Action Buttons Selectors */

export const createNegotiationDetailsToTopLevelDetailsTransformerSelector = (
  itemNegotiationsSelector: Selector<GlobalState, NegotiationDetail[]>,
  summaryNegotiationsSelector: Selector<GlobalState, NegotiationDetail[]>
): Selector<GlobalState, (negotiationDetails: NegotiationDetail[]) => NegotiationDetail[]> =>
  createSelector(
    itemNegotiationsSelector,
    summaryNegotiationsSelector,
    (allItemNegotiationsDetails, allSummaryNegotiationDetails) => (negotiationDetails: NegotiationDetail[]) => {
      // Any item negotiations will be grouped by summary negotiation Id whereas all individual or summary negotiations
      // will be grouped by 'na'
      const negotiationDetailsBySummaryId = groupBy(
        negotiationDetails,
        negotiationDetail => negotiationDetail?.negotiation?.summaryNegotiationId || 'na'
      )

      return flatten(
        Object.entries(negotiationDetailsBySummaryId).map(([summaryId, negotiationDetailsForSummaryId]) => {
          if (
            negotiationDetailsForSummaryId.length === 1 &&
            negotiationDetailsForSummaryId[0]?.ownOrder &&
            negotiationDetailsForSummaryId[0]?.negotiation?.negotiationType === NegotiationType.AON_X_SUMMARY
          ) {
            // for AONX get all nested AON summary actions
            return allSummaryNegotiationDetails.filter(
              s =>
                s.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY &&
                s.negotiation.containerCollectionId === negotiationDetailsForSummaryId[0].negotiation.containerCollectionId
            )
          }

          // If not in a AoN collection, return original negotiation details
          if (summaryId === 'na') {
            return negotiationDetailsForSummaryId
          }

          const isOcoPackage = negotiationDetailsForSummaryId[0].negotiation.negotiationType === NegotiationType.ONE_CANCELS_OTHERS_ITEM
          const isSmartGroup = negotiationDetailsForSummaryId[0].negotiation.negotiationType === NegotiationType.SMART_GROUPING_ITEM

          // If there is a single live OCO item negotiation, then just return the item negotiation
          if (negotiationDetailsForSummaryId.length === 1 && (isOcoPackage || isSmartGroup)) {
            return negotiationDetailsForSummaryId
          }

          const allItemNegotiationDetailsForSummaryId = allItemNegotiationsDetails.filter(
            negotiationDetail => negotiationDetail.negotiation.summaryNegotiationId === summaryId
          )

          const someCanBeAccepted = negotiationDetailsForSummaryId.some(canAccept)

          const summaryNegotiationDetail = allSummaryNegotiationDetails.find(
            negotiationDetail => negotiationDetail.negotiation.negotiationId === summaryId
          )

          if (isOcoPackage && someCanBeAccepted && !isNil(summaryNegotiationDetail)) {
            return [summaryNegotiationDetail]
          }

          // Are all negotiations for a package selected?
          if (allItemNegotiationDetailsForSummaryId.length === negotiationDetailsForSummaryId.length) {
            if (isNil(summaryNegotiationDetail)) {
              return negotiationDetailsForSummaryId
            }

            if (
              summaryNegotiationDetail.ownOrder &&
              summaryNegotiationDetail.negotiation.negotiationType === NegotiationType.SMART_GROUPING_SUMMARY
            ) {
              return [summaryNegotiationDetail]
            }

            return summaryNegotiationDetail.negotiation.negotiationType !== NegotiationType.CONTINGENT_SUMMARY && !someCanBeAccepted
              ? negotiationDetailsForSummaryId
              : [summaryNegotiationDetail]
          }

          return negotiationDetailsForSummaryId
        })
      )
    }
  )

export const initiatedNegotiationDetailsToTopLevelDetailsTransformerSelector = createNegotiationDetailsToTopLevelDetailsTransformerSelector(
  initiatedItemNegotiationsSelector,
  initiatedSummaryNegotiationsSelector
)

export const incomingNegotiationDetailsToTopLevelDetailsTransformerSelector = createNegotiationDetailsToTopLevelDetailsTransformerSelector(
  createIncomingItemNegotiationsSelector(),
  createIncomingSummaryNegotiationsSelector()
)

export const initiatedNegotiationsCountByRfqIdGetterSelector: Selector<GlobalState, (rfqId: string) => number> = createSelector(
  [initiatedItemNegotiationsSelector],
  initiatedItemNegotiationDetails => rfqId =>
    initiatedItemNegotiationDetails.filter(negotiationDetail => negotiationDetail.negotiation.parentOrderId === rfqId).length
)

export const initiatedSummaryNegotiationsCountByCollectionIdGetterSelector: Selector<
  GlobalState,
  (collectionId: string) => number
> = createSelector([initiatedSummaryNegotiationsSelector], initiatedSummaryNegotiationDetails => collectionId =>
  initiatedSummaryNegotiationDetails.filter(negotiationDetail => negotiationDetail.negotiation.collectionId === collectionId).length
)

export const initiatedNegotiationsCountByCollectionIdGetterSelector: Selector<
  GlobalState,
  (collectionId: string, negotiationType: NegotiationType) => number
> = createSelector(
  [initiatedItemNegotiationsSelector, initiatedNegotiationDetailsToTopLevelDetailsTransformerSelector],
  (initiatedItemNegotiationDetails, topLevelDetailsTransformer) => collectionId => {
    const topLevelNegotiationDetails = topLevelDetailsTransformer(initiatedItemNegotiationDetails)
    return topLevelNegotiationDetails.filter(negotiationDetail => negotiationDetail.negotiation.collectionId === collectionId).length
  }
)

export const hasInitiatedCollectionOnlyOneCounterpartySelector: Selector<GlobalState, (collectionId: string) => boolean> = createSelector(
  [initiatedItemNegotiationsSelector, initiatedNegotiationDetailsToTopLevelDetailsTransformerSelector],
  (initiatedItemNegotiationDetails, topLevelDetailsTransformer) => (collectionId: string) => {
    const topLevelNegotiationDetails = topLevelDetailsTransformer(initiatedItemNegotiationDetails)
    const negotiationsWithinCollection = topLevelNegotiationDetails.filter(n => n.negotiation.collectionId === collectionId)

    return uniq(negotiationsWithinCollection, n => n.counterparty?.id).length === 1
  }
)

/* Toolbar Button Selectors */

export const incomingNegotiationDetailsByRfqIdGetterSelector: Selector<
  GlobalState,
  (rfqId: string) => NegotiationDetail[]
> = createSelector([createIncomingItemNegotiationsSelector()], incomingItemNegotiationDetails => rfqId =>
  incomingItemNegotiationDetails.filter(negotiationDetail => negotiationDetail.negotiation.parentOrderId === rfqId)
)

export const incomingNegotiationDetailsByNegotiationIdGetterSelector: Selector<
  GlobalState,
  (negotiationId: string) => NegotiationDetail[]
> = createSelector([createIncomingItemNegotiationsSelector()], incomingItemNegotiationDetails => negotiationId =>
  incomingItemNegotiationDetails.filter(negotiationDetail => negotiationDetail.negotiation.negotiationId === negotiationId)
)

export const incomingNegotiationDetailsByCollectionId: Selector<
  GlobalState,
  (collectionId: string) => NegotiationDetail[]
> = createSelector([createIncomingItemNegotiationsSelector()], incomingItemNegotiationDetails => collectionId =>
  incomingItemNegotiationDetails.filter(negotiationDetail => negotiationDetail.negotiation.collectionId === collectionId)
)

export const allCancellableNegotiationIdsSelector: Selector<GlobalState, string[]> = createSelector(
  createNegotiationEntrySelector({
    negotiationFilter: NegotiationFilter.InitiatorOnly,
    negotiationTypes: [
      NegotiationType.CONTINGENT_SUMMARY,
      NegotiationType.ONE_CANCELS_OTHERS_SUMMARY,
      NegotiationType.INDIVIDUAL,
      NegotiationType.SMART_GROUPING_SUMMARY
    ],
    subscriptionIdentifier: NegotiationSubscriptionIdentifier.INITIATED
  }),
  negotiationEntries =>
    negotiationEntries
      .filter(negotiationEntry => negotiationEntry.lastUpdate.initiatorAvailableEvents.includes(NegotiationEvent.CANCEL))
      .map(negotiationEntry => negotiationEntry.lastUpdate.entityId)
)

export const allBackToStagingNegotiationIdsSelector: Selector<GlobalState, string[]> = createSelector(
  createNegotiationEntrySelector({
    negotiationFilter: NegotiationFilter.InitiatorOnly,
    negotiationTypes: [
      NegotiationType.CONTINGENT_SUMMARY,
      NegotiationType.ONE_CANCELS_OTHERS_SUMMARY,
      NegotiationType.INDIVIDUAL,
      NegotiationType.SMART_GROUPING_SUMMARY
    ],
    subscriptionIdentifier: NegotiationSubscriptionIdentifier.INITIATED
  }),
  negotiationEntries =>
    negotiationEntries
      .filter(
        negotiationEntry =>
          negotiationEntry.lastUpdate.initiatorAvailableEvents.includes(NegotiationEvent.BACK_TO_STAGING) &&
          negotiationEntry.lastUpdate.entity.canSendBackToStaging &&
          !negotiationEntry.lastUpdate.entity.sentBackToStaging
      )
      .map(negotiationEntry => negotiationEntry.lastUpdate.entityId)
)

/* Nav Bar Selectors */

export const incomingNegotiationEntrySelector = createNegotiationEntrySelector({
  negotiationFilter: NegotiationFilter.ResponderOnly,
  statusSelector: incomingNegotiationsStatusSelector,
  negotiationTypes: itemNegotiationTypes,
  subscriptionIdentifier: NegotiationSubscriptionIdentifier.INCOMING
})

export const incomingNegotiationsNumberOfItemsSelector: Selector<GlobalState, number> = createSelector(
  incomingNegotiationEntrySelector,
  negotiationEntries => negotiationEntries.filter(({ lastUpdate }) => !isNegotiationCurve(lastUpdate.entity)).length
)

export const initiatedNegotiationsNumberOfItemsSelector: Selector<GlobalState, number> = createSelector(
  createOrgActivityFilteredNegotiationEntrySelector({
    negotiationFilter: NegotiationFilter.InitiatorOnly,
    statusSelector: createInitiatedNegotiationsStatusSelector(),
    negotiationTypes: itemNegotiationTypes,
    hideNegotiationSentToStaging: true,
    subscriptionIdentifier: NegotiationSubscriptionIdentifier.INITIATED
  }),
  negotiationEntries => negotiationEntries.length
)

export function createNegotiationCollectionDetailsSelector(
  gridId: GridId,
  negotiationFilter: NegotiationFilter,
  subscriptionIdentifier: NegotiationSubscriptionIdentifier,
  statusSelector: Selector<GlobalState, NegotiationStatus[]>,
  negotiationTypes: NegotiationType[]
): Selector<GlobalState, CollectionDetail[]> {
  const negotiationSelector = createOrgActivityFilteredNegotiationEntrySelector({
    negotiationFilter,
    statusSelector,
    negotiationTypes,
    hideNegotiationSentToStaging: true,
    subscriptionIdentifier
  })

  return createSelector(
    [
      negotiationSelector,
      createEntityGetterSelector(EntityType.COLLECTION),
      createSelectedCollectionIdSelector(gridId),
      currentOrganisationIdSelector
    ],
    (negotiations, collectionGetter, selectedCollectionId, currentOrganisationId) => {
      // if in admin global view don't list collections
      if (!currentOrganisationId) {
        return []
      }
      const collectionRecords = negotiations.reduce((previousCollectionRecords, negotiationUpdate) => {
        const negotiation = negotiationUpdate.lastUpdate.entity
        const ownOrder = negotiationUpdate.lastUpdate.negotiationSide === NegotiationSide.INITIATOR

        const candidateCollectionId = ownOrder ? negotiation.containerCollectionId ?? negotiation.collectionId : negotiation.collectionId

        // if collection id assigned but collection is not available don't add it
        const collection = candidateCollectionId ? collectionGetter(candidateCollectionId) : null
        if (candidateCollectionId && !collection) {
          return previousCollectionRecords
        }

        const collectionId = candidateCollectionId || UnassignedCollectionId
        const collectionName = collection ? collection.name : UnassignedCollectionName
        if (!previousCollectionRecords[collectionId]) {
          previousCollectionRecords[collectionId] = newCollectionDetail(collectionId, collectionName)
        }

        const previousCollectionDetail = previousCollectionRecords[collectionId]
        previousCollectionDetail.totalItems += 1
        if (negotiation.status === NegotiationStatus.REQUESTED_WITHOUT_PRICE) {
          previousCollectionDetail.subTotals[0] += 1
        } else if (negotiation.status === NegotiationStatus.QUOTED) {
          previousCollectionDetail.subTotals[1] += 1
        } else if (negotiation.status === NegotiationStatus.REQUESTED_WITH_PRICE) {
          previousCollectionDetail.subTotals[2] += 1
        }

        const isActiveStatus = ![
          NegotiationStatus.EXPIRED,
          NegotiationStatus.REJECTED,
          NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_REJECTED_EXPIRED
        ].includes(negotiation.status)

        if (isActiveStatus) {
          if (!previousCollectionDetail.minRfqEndTime || moment(negotiation.rfqEndTime).isBefore(previousCollectionDetail.minRfqEndTime)) {
            previousCollectionDetail.minRfqEndTime = negotiation.rfqEndTime
          }

          if (
            negotiation.otwEndTime &&
            (!previousCollectionDetail.minOtwEndTime || moment(negotiation.otwEndTime).isBefore(previousCollectionDetail.minOtwEndTime))
          ) {
            previousCollectionDetail.minOtwEndTime = negotiation.otwEndTime
          }
        }

        return previousCollectionRecords
      }, {} as Record<string, CollectionDetail>)

      return toCollectionDetails(collectionRecords, selectedCollectionId, collectionGetter)
    }
  )
}

export const incomingNegotiationsCollectionDetailsSelector = createNegotiationCollectionDetailsSelector(
  GridId.IncomingGrid,
  NegotiationFilter.ResponderOnly,
  NegotiationSubscriptionIdentifier.INCOMING,
  incomingNegotiationsStatusSelector,
  topLevelNegotiationTypes.filter(n => n !== NegotiationType.SMART_GROUPING_SUMMARY)
)

export const initiatedNegotiationsCollectionDetailsSelector = createNegotiationCollectionDetailsSelector(
  GridId.InitiatedGrid,
  NegotiationFilter.InitiatorOnly,
  NegotiationSubscriptionIdentifier.INITIATED,
  createInitiatedNegotiationsStatusSelector(),
  topLevelNegotiationTypes
)
